/**
 * Helpers for the user-controlled Ally CPO valuation (SWA-7938).
 *
 * "Apply CPOV" on Lender Vehicle Search and "CPOV" in Adjust Deal both hinge on two questions:
 * is the lender even configured with the Ally CPO guidebook, and did the deal source hand us a
 * certified pre-owned vehicle (by condition, or by an R1 comment saying so - SWA-7986) to
 * pre-check the box for.
 */
import { cleanDecisionComments } from 'components/Modals/LoanVariablesDialog/utils/decisionComments';

/** `internal_value` the backend reports for the Ally CPO guidebook (GUIDEBOOK_TO_FIELD_MAP). */
export const ALLY_CPO_GUIDEBOOK_INTERNAL_VALUE = 'ally_cpo';

const CERTIFIED_USED_CONDITION = 'CERTIFIED USED';
const CPOV_COMMENT_PHRASE = 'cpov required documentation must be included with contract package';

/**
 * Deal sources disagree on how to spell the certified pre-owned condition: RouteOne sends
 * `CERTIFIED USED`, the deal jacket builder rewrites it to `CERTIFIED_USED`, and DealerTrack
 * derives it from a `certifiedUsed` flag. Normalize separators, casing and stray whitespace
 * before comparing rather than matching any single spelling.
 */
export const isCertifiedUsedCondition = (condition?: string | null): boolean => {
  if (!condition) return false;

  return (
    condition
      .trim()
      .replace(/[\s_]+/g, ' ')
      .toUpperCase() === CERTIFIED_USED_CONDITION
  );
};

/** True when the lender has the Ally CPO guidebook configured, which is what gates the controls. */
export const isAllyCpoConfiguredForLender = (guidebooks?: Record<string, string>[] | null): boolean =>
  Boolean(guidebooks?.some((guidebook) => guidebook?.internal_value === ALLY_CPO_GUIDEBOOK_INTERNAL_VALUE));

/**
 * True when an R1 decision comment declares the vehicle CPO, independent of its condition.
 * `decision_comments` packs a whole decision into one pipe/`<br>`-delimited string plus RouteOne's
 * own `$$...$$` button markup, so it is parsed with the same util the Adjust Deal comments note
 * uses rather than matched as a raw substring.
 */
export const hasCpovDecisionComment = (comments?: string[] | null): boolean =>
  cleanDecisionComments(comments).some((segment) => segment.toLowerCase().includes(CPOV_COMMENT_PHRASE));
